import { useEffect, useRef, useState } from 'react';
import { ArrowsUpDownIcon } from '@heroicons/react/24/outline';
import FilterChip from '../../../../components/FilterChip';
import { ACTIVITY_FILTERS, ROLE_FILTERS, TERMS_FILTERS } from '../../../../utils/userFilters';

export const SORT_OPTIONS = [
  { value: 'name', label: 'Name' },
  { value: 'email', label: 'Email' },
  { value: 'date_joined', label: 'Joined date' },
  { value: 'last_login', label: 'Last seen' },
  { value: 'is_verified', label: 'Verified' },
];

const FILTERS = [
  { key: 'role', label: 'Role', options: ROLE_FILTERS },
  { key: 'activity', label: 'Activity', options: ACTIVITY_FILTERS },
  { key: 'legal', label: 'Terms', options: TERMS_FILTERS },
];

const popoverClass = 'absolute z-50 mt-2 min-w-[13rem] rounded-lg bg-white dark:bg-gray-700 border border-gray-200/70 dark:border-gray-600/70 shadow-lg p-1.5';
const optionClass = (on) => `w-full flex items-center justify-between gap-4 text-left px-2 py-1.5 rounded text-sm ${
  on
    ? 'bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300 font-semibold'
    : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600'
}`;

// Desktop toolbar chips of the admin Users page: Role, Activity and Terms of
// Use filters plus Sort, as FilterChips with option popovers (one open at a
// time, the Sessions filter bar pattern). Each filter option shows how many
// users it would leave given the other filters (`countFor(key, value)`); a chip
// that is filtering stays highlighted.
export default function UserFilterBar({ filters, onChange, sortBy, onSortChange, countFor }) {
  const [open, setOpen] = useState(null);
  const barRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onOutside = (e) => { if (barRef.current && !barRef.current.contains(e.target)) setOpen(null); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(null); };
    document.addEventListener('mousedown', onOutside);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onOutside);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggle = (key) => setOpen((o) => (o === key ? null : key));

  return (
    <div ref={barRef} className="flex flex-wrap items-center gap-2">
      {FILTERS.map(({ key, label, options }) => {
        const current = options.find((o) => o.value === filters[key]) || options[0];
        return (
          <div key={key} className="relative">
            <FilterChip
              label={label}
              value={current.label}
              active={open === key}
              highlighted={filters[key] !== 'all'}
              onClick={() => toggle(key)}
            />
            {open === key && (
              <div role="menu" aria-label={`Filter by ${label}`} className={`${popoverClass} left-0`}>
                {options.map((o) => (
                  <button
                    key={o.value}
                    type="button"
                    role="menuitemradio"
                    aria-checked={filters[key] === o.value}
                    className={optionClass(filters[key] === o.value)}
                    onClick={() => { onChange(key, o.value); setOpen(null); }}
                  >
                    <span>{o.label}</span>
                    <span className="font-mono text-xs tabular-nums text-gray-400 dark:text-gray-400">{countFor(key, o.value)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })}

      <span className="mx-0.5 h-5 w-px bg-gray-200/70 dark:bg-gray-600/70" aria-hidden="true" />

      <div className="relative">
        <FilterChip
          icon={ArrowsUpDownIcon}
          value={(SORT_OPTIONS.find((o) => o.value === sortBy) || SORT_OPTIONS[0]).label}
          title="Sort by"
          active={open === 'sort'}
          onClick={() => toggle('sort')}
        />
        {open === 'sort' && (
          <div role="menu" aria-label="Sort by" className={`${popoverClass} right-0`}>
            {SORT_OPTIONS.map((o) => (
              <button
                key={o.value}
                type="button"
                role="menuitemradio"
                aria-checked={sortBy === o.value}
                className={optionClass(sortBy === o.value)}
                onClick={() => { onSortChange(o.value); setOpen(null); }}
              >
                {o.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
